import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUsers, createUser, updateUser, deleteUser } from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit, Trash2, Users, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  superadmin: 'Superadmin',
  admin: 'Admin',
  professor: 'Profesor',
  student: 'Estudiante',
};

const ROLE_COLORS = {
  superadmin: 'destructive',
  admin: 'default',
  professor: 'secondary',
  student: 'outline',
};

export const ManageUsers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('student');
  const [userStatus, setUserStatus] = useState('active');

  useEffect(() => {
    if (user && user.role !== 'superadmin') {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setEmail('');
    setPassword('');
    setRole('student');
    setUserStatus('active');
    setEditingUser(null);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setName(u.name);
    setEmail(u.email);
    setPassword('');
    setRole(u.role);
    setUserStatus(u.status);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { name, role, status: userStatus };
        await updateUser(editingUser.id, updateData);
        toast.success('Usuario actualizado');
      } else {
        if (!password) {
          toast.error('La contraseña es requerida');
          return;
        }
        await createUser({ name, email, password, role });
        toast.success(`Usuario ${ROLE_LABELS[role]} creado`);
      }
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`¿Eliminar a ${userName}? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteUser(userId);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  if (loading) return <div className="p-8">Cargando...</div>;

  const superadmins = users.filter(u => u.role === 'superadmin');
  const admins = users.filter(u => u.role === 'admin');
  const professors = users.filter(u => u.role === 'professor');
  const students = users.filter(u => u.role === 'student');

  return (
    <div className="p-6 md:p-12 bg-secondary min-h-screen" data-testid="manage-users-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary mb-2 flex items-center gap-3">
              <ShieldCheck className="w-8 h-8" />
              Gestionar Usuarios
            </h1>
            <p className="text-muted-foreground">
              Crea y administra usuarios de todos los roles — {users.length} usuarios totales
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} data-testid="create-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Usuario
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="user-name">Nombre completo *</Label>
                  <Input
                    id="user-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="user-email">Email *</Label>
                  <Input
                    id="user-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={!!editingUser}
                    data-testid="user-email-input"
                  />
                  {editingUser && <p className="text-xs text-muted-foreground">El email no puede editarse</p>}
                </div>
                {!editingUser && (
                  <div className="space-y-2">
                    <Label htmlFor="user-password">Contraseña *</Label>
                    <Input
                      id="user-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="Mínimo 6 caracteres"
                      data-testid="user-password-input"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Rol *</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="superadmin">Superadmin</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="professor">Profesor</SelectItem>
                      <SelectItem value="student">Estudiante</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editingUser && (
                  <div className="space-y-2">
                    <Label>Estado</Label>
                    <Select value={userStatus} onValueChange={setUserStatus}>
                      <SelectTrigger data-testid="user-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Activo</SelectItem>
                        <SelectItem value="blocked">Bloqueado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {role === 'superadmin' && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
                    <strong>Advertencia:</strong> Los superadmins tienen acceso completo a toda la plataforma.
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button type="submit" data-testid="save-user-btn">
                    {editingUser ? 'Actualizar' : 'Crear'} Usuario
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Superadmins', count: superadmins.length, color: 'text-destructive' },
            { label: 'Admins', count: admins.length, color: 'text-primary' },
            { label: 'Profesores', count: professors.length, color: 'text-blue-600' },
            { label: 'Estudiantes', count: students.length, color: 'text-green-600' },
          ].map(({ label, count, color }) => (
            <Card key={label}>
              <CardContent className="p-4 text-center">
                <p className={`text-2xl font-bold ${color}`}>{count}</p>
                <p className="text-sm text-muted-foreground">{label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* User list */}
        <div className="space-y-3">
          {users.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No hay usuarios registrados</p>
              </CardContent>
            </Card>
          ) : (
            users.map((u) => (
              <Card key={u.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{u.name}</p>
                          <Badge variant={ROLE_COLORS[u.role]}>{ROLE_LABELS[u.role]}</Badge>
                          {u.status !== 'active' && (
                            <Badge variant="destructive">{u.status}</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEdit(u)}
                        data-testid={`edit-user-${u.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {u.id !== user.id && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(u.id, u.name)}
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageUsers;
